import React from "react";
import { Box, Menu, PopoverOrigin, SxProps, Theme, Typography, useTheme } from "@mui/material";
import { useTouchLayout } from "../../hooks/useTouchLayout";
import BottomSheet from "./BottomSheet";
import { overlaySurface, floatShadow, panelBorder } from "../Dashboard/overviewPanel";

interface AdaptivePopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** Uppercase section label above the options. */
  title?: string;
  /** Pointer idiom only: where the menu hangs off its anchor. Defaults to below-left. */
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  /** Pointer idiom only: overrides for the menu paper (e.g. a wider panel). */
  paperSx?: SxProps<Theme>;
  children: React.ReactNode;
}

/**
 * A list of options and actions, presented in whichever idiom fits the input
 * device: an anchored menu on pointer, a bottom sheet on touch. Callers write
 * the rows once and get both.
 */
const AdaptivePopover: React.FC<AdaptivePopoverProps> = ({
  open,
  anchorEl,
  onClose,
  title,
  anchorOrigin = { vertical: "bottom", horizontal: "left" },
  transformOrigin = { vertical: "top", horizontal: "left" },
  paperSx,
  children,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isTouch = useTouchLayout();

  if (isTouch) {
    return (
      <BottomSheet open={open} onClose={onClose} title={title}>
        {children}
      </BottomSheet>
    );
  }

  return (
    <Menu
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      elevation={0}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      slotProps={{
        paper: {
          sx: [
            {
              mt: 0.5,
              minWidth: 220,
              maxWidth: 320,
              maxHeight: 360,
              // The overlay family: the ticker page's card grey, one outline,
              // and a soft shadow because a menu can open over a dialog of
              // the same colour.
              borderRadius: 1.5,
              backgroundImage: "none",
              boxShadow: floatShadow(isDark),
              backgroundColor: overlaySurface(isDark),
              border: `1px solid ${panelBorder(isDark)}`,
            },
            ...(Array.isArray(paperSx) ? paperSx : [paperSx ?? false]),
          ],
        },
        list: { sx: { py: 0.5 } },
      }}
    >
      {title && (
        <Typography
          sx={{
            px: 1.5,
            pt: 0.5,
            pb: 1,
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: theme.palette.text.secondary,
          }}
        >
          {title}
        </Typography>
      )}
      <Box sx={{ px: 0.5 }}>{children}</Box>
    </Menu>
  );
};

export default AdaptivePopover;
